import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Save, RotateCcw } from 'lucide-react';
import { useTheme } from '../theme/ThemeProvider';
import { ThemeColors, THEMES_PREDEFINIS } from '../theme/themes';
import { configurationApi } from '../api/configuration.api';

// ── Utilitaires hex ↔ RGB ────────────────────────────────────────────────────
function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}
function rgbToHex(rgb: string): string {
  const [r, g, b] = rgb.split(' ').map(Number);
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

// ── Groupes de couleurs pour la personnalisation ─────────────────────────────
type ColorKey = keyof ThemeColors;

const COLOR_GROUPS: { titre: string; champs: { key: ColorKey; label: string }[] }[] = [
  {
    titre: 'Couleurs primaires',
    champs: [
      { key: 'primary',      label: 'Primaire' },
      { key: 'primaryHover', label: 'Primaire (survol)' },
      { key: 'primaryLight', label: 'Primaire clair' },
      { key: 'primaryDark',  label: 'Primaire foncé' },
      { key: 'primaryText',  label: 'Texte primaire' },
      { key: 'primaryMuted', label: 'Primaire atténué' },
    ],
  },
  {
    titre: 'Surfaces & fonds',
    champs: [
      { key: 'surface',        label: 'Surface principale' },
      { key: 'surfaceRaised',  label: 'Surface élevée' },
      { key: 'surfaceOverlay', label: 'Surface overlay' },
    ],
  },
  {
    titre: 'Textes & contenus',
    champs: [
      { key: 'content',         label: 'Texte principal' },
      { key: 'contentMuted',    label: 'Texte atténué' },
      { key: 'contentSubtle',   label: 'Texte subtil' },
      { key: 'contentInverted', label: 'Texte inversé' },
    ],
  },
  {
    titre: 'Bordures',
    champs: [
      { key: 'border',       label: 'Bordure' },
      { key: 'borderStrong', label: 'Bordure forte' },
    ],
  },
  {
    titre: 'États sémantiques',
    champs: [
      { key: 'success',      label: 'Succès' },
      { key: 'successLight', label: 'Succès clair' },
      { key: 'warning',      label: 'Avertissement' },
      { key: 'warningLight', label: 'Avertissement clair' },
      { key: 'danger',       label: 'Danger' },
      { key: 'dangerLight',  label: 'Danger clair' },
    ],
  },
  {
    titre: 'Navigation (sidebar)',
    champs: [
      { key: 'sidebarBg',     label: 'Fond sidebar' },
      { key: 'sidebarText',   label: 'Texte sidebar' },
      { key: 'sidebarActive', label: 'Lien actif' },
      { key: 'sidebarHover',  label: 'Lien (survol)' },
      { key: 'sidebarBorder', label: 'Bordure sidebar' },
    ],
  },
];

// ── Aperçu d'un thème ────────────────────────────────────────────────────────
function ThemePreview({ couleurs }: { couleurs: ThemeColors }) {
  const toStyle = (rgb: string) => `rgb(${rgb.replace(/ /g, ',')})`;
  return (
    <div className="flex gap-1.5 items-center">
      <div className="w-5 h-5 rounded-full border border-black/10 flex-shrink-0"
           style={{ background: toStyle(couleurs.primary) }} />
      <div className="w-5 h-5 rounded-full border border-black/10 flex-shrink-0"
           style={{ background: toStyle(couleurs.sidebarBg) }} />
      <div className="w-5 h-5 rounded-full border border-black/10 flex-shrink-0"
           style={{ background: toStyle(couleurs.surface) }} />
      <div className="w-5 h-5 rounded-full border border-black/10 flex-shrink-0"
           style={{ background: toStyle(couleurs.success) }} />
    </div>
  );
}

export function ThemePage() {
  const { theme, setTheme } = useTheme();
  const [slugSelectionne, setSlugSelectionne] = useState(theme.slug);
  const [custom, setCustom] = useState<Partial<ThemeColors>>({});
  const [accordeonOuvert, setAccordeonOuvert] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Couleurs effectives = thème prédéfini + custom overrides
  const themeBase = THEMES_PREDEFINIS.find(t => t.slug === slugSelectionne) ?? THEMES_PREDEFINIS[0];
  const couleursEffectives: ThemeColors = { ...themeBase.couleurs, ...custom };

  const handleSelectTheme = (slug: string) => {
    setSlugSelectionne(slug);
    setCustom({});
    setTheme(slug);
  };

  const handleColorChange = (key: ColorKey, hex: string) => {
    const rgb = hexToRgb(hex);
    const updated = { ...custom, [key]: rgb };
    setCustom(updated);
    setTheme(slugSelectionne, updated);
  };

  const handleSauvegarder = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await configurationApi.sauvegarderTheme(slugSelectionne, Object.keys(custom).length > 0 ? custom as Record<string, string> : undefined);
      setMessage({ type: 'success', text: 'Thème sauvegardé avec succès' });
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleReinitialiser = () => {
    setCustom({});
    setTheme(slugSelectionne);
    setMessage(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-content">Apparence & Thème</h1>
        <p className="text-content-muted text-sm mt-1">
          Personnalisez les couleurs de l'application. Le thème s'applique immédiatement.
        </p>
      </div>

      {/* Section thèmes prédéfinis */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-content mb-4">Thèmes prédéfinis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {THEMES_PREDEFINIS.map((t) => {
            const actif = t.slug === slugSelectionne && Object.keys(custom).length === 0;
            return (
              <button
                key={t.slug}
                onClick={() => handleSelectTheme(t.slug)}
                className={`relative p-4 rounded-xl border-2 text-left transition-all
                  ${actif
                    ? 'border-primary bg-primary-light shadow-md'
                    : 'border-border bg-surface hover:border-primary-muted hover:shadow-sm'
                  }`}
              >
                {actif && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-content-inverted" />
                  </span>
                )}
                <ThemePreview couleurs={t.couleurs} />
                <p className="mt-2 font-semibold text-sm text-content">{t.nom}</p>
                <p className="text-xs text-content-muted mt-0.5">{t.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Section personnalisation avancée */}
      <section className="mb-8">
        <button
          onClick={() => setAccordeonOuvert(!accordeonOuvert)}
          className="w-full flex items-center justify-between p-4 bg-surface-raised border border-border rounded-xl hover:bg-surface-overlay transition-colors"
        >
          <div className="text-left">
            <p className="font-semibold text-content">Personnalisation avancée</p>
            <p className="text-xs text-content-muted mt-0.5">
              Color picker par catégorie — {Object.keys(custom).length > 0 ? `${Object.keys(custom).length} couleur(s) modifiée(s)` : 'aucune modification'}
            </p>
          </div>
          {accordeonOuvert
            ? <ChevronUp className="w-5 h-5 text-content-muted" />
            : <ChevronDown className="w-5 h-5 text-content-muted" />
          }
        </button>

        {accordeonOuvert && (
          <div className="mt-3 border border-border rounded-xl overflow-hidden divide-y divide-border">
            {COLOR_GROUPS.map((group) => (
              <div key={group.titre} className="p-4 bg-surface">
                <p className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-3">
                  {group.titre}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {group.champs.map(({ key, label }) => {
                    const currentRgb = couleursEffectives[key];
                    const hex = rgbToHex(currentRgb);
                    const isOverridden = key in custom;
                    return (
                      <label key={key} className="flex items-center gap-2 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="color"
                            value={hex}
                            onChange={(e) => handleColorChange(key, e.target.value)}
                            className="w-8 h-8 rounded-lg border border-border cursor-pointer"
                            title={label}
                          />
                          {isOverridden && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-primary border-2 border-surface" />
                          )}
                        </div>
                        <span className={`text-xs ${isOverridden ? 'text-primary font-medium' : 'text-content-muted'}`}>
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Message retour */}
      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium
          ${message.type === 'success' ? 'bg-success-light text-success' : 'bg-danger-light text-danger'}`}>
          {message.text}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSauvegarder}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-content-inverted rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 text-sm font-medium"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        <button
          onClick={handleReinitialiser}
          disabled={Object.keys(custom).length === 0}
          className="flex items-center gap-2 px-5 py-2.5 border border-border text-content rounded-lg hover:bg-surface-raised transition-colors disabled:opacity-40 text-sm font-medium"
        >
          <RotateCcw className="w-4 h-4" />
          Réinitialiser
        </button>
      </div>
    </div>
  );
}
